<template>
  <div>
    <el-row class="title-bar" type="flex" justify="space-between">
      <div class="vertical-center-box">
        <div class="title-h3 mr-r15">수집장비(RTU) 리스트</div>
        <el-button class="mr-r4" type="primary" @click="onDblClickForSlidingPopup">수집장비등록</el-button>
        <el-button :disabled="selectedRowArray.length === 0" @click="onDelRtuDevice">장비 삭제</el-button>
      </div>
      <div class="vertical-center-box">
        <el-button class="mr-r4" type="primary" size="mini" @click="saveRtu" :disabled="!cellEditing.isModified">저장</el-button>
        <el-button class="mr-r4" size="mini" @click="loadData" :disabled="!cellEditing.isModified">취소</el-button>
        <w-header-search-button size="mini" @click="setSearchToolVisible"></w-header-search-button>
      </div>
    </el-row>
    <el-table-wrapper
      v-loading="isLoading"
      :editable="true"
      @selection-change="changeHandleSelection"
      :data="tableData"
      :columns="tableColumn"
      :pagination="true"
      :show-search-tool="showSearchTool"
      ref="rtuTable"
      style="width:100%;"
      :show-custom-header="true"
      :column-default="columnDefault"
      @condition-change="onChangeTable"
      @cell-dblclick="startEdit"
      :total="total"
      remote
    >
      <template #input-slot="scope">
        <w-input-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" :required="true" @cell-edit-end="endEdit" />
      </template>
      <template #iptype-slot="scope">
        <w-select-editor
          :editing-prop="cellEditing.property"
          :data="scope.row"
          :property="scope.column.property"
          :required="true"
          :list="[
            { value: 'IPv4', label: 'IPv4' },
            { value: 'IPv6', label: 'IPv6' },
          ]"
          keyField="value"
          labelField="label"
          valueField="value"
          @cell-edit-end="endEdit"
        />
      </template>
      <template #ipaddress-slot="scope">
        <w-input-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" :required="true" :validation="validateIpaddress" @cell-edit-end="endIpaddressEdit" />
      </template>
      <template #tcp-slot="scope">
        <w-number-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" :min="0" @cell-edit-end="endEdit" />
      </template>
      <template #snmp-version-slot="scope">
        <w-select-editor
          :editing-prop="cellEditing.property"
          :data="scope.row"
          :property="scope.column.property"
          :required="true"
          :list="snmpVersionList"
          keyField="basicinfoSubid"
          labelField="basicinfoSubname"
          valueField="basicinfoSubid"
          @cell-edit-end="endEdit"
        />
      </template>
      <template #community-slot="scope">
        <w-input-editor type="password" :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" @cell-edit-end="endEdit">
          <span slot="content">{{ convertCommunityRead(scope.row) }}</span>
        </w-input-editor>
      </template>
      <template #snmp-slot="scope">
        <w-number-editor :editing-prop="cellEditing.property" :data="scope.row" :property="scope.column.property" :min="0" @cell-edit-end="endEdit" />
      </template>
    </el-table-wrapper>
    <w-rtu-device-sliding-popup ref="rtuPop" @load-data="loadData" :snmp-version-list="snmpVersionList"></w-rtu-device-sliding-popup>
  </div>
</template>

<script>
import { isNil, cloneDeep, isEqual } from 'lodash';
import { columnDefault } from '@/constants/table';
import { getDateToString } from '@/utils/date';
import { EMPTY_TEXT, validateIpv4, validateIpv6 } from '@/utils/string';
import msgUtil from '@/utils/message';

import DeviceRegistrationFmsApi from '@/api/settings/common/device-registration-fms';
import BasicinfoSubAPI from '@/api/model/basicinfo-sub';
import CellEditingMixin from '@/mixins/table/cell-editing';
import DeviceRegistrationCommonAPI from '@/api/settings/common/device-registration-common';
import { getRsaEncryptBulk } from '@/utils/rsa-encrypt';
import WInputEditor from '@/components/table/cell-editor/InputEditor';
import WSelectEditor from '@/components/table/cell-editor/SelectEditor';
import WNumberEditor from '@/components/table/cell-editor/NumberEditor';
import WRtuDeviceSlidingPopup from './popup/RtuDeviceSlidingPopup';

export default {
  name: 'WRtuList',
  mixins: [CellEditingMixin],
  components: {
    WRtuDeviceSlidingPopup,
    WInputEditor,
    WSelectEditor,
    WNumberEditor,
  },
  data() {
    return {
      isLoading: false,
      total: null,
      columnDefault,
      showSearchTool: false,
      currentPage: 1,
      selectedRowArray: [],
      tableData: [],
      originalTableData: [],
      snmpVersionList: [],
      tableColumn: [
        {
          // align: 'center',
          type: 'selection',
        },
        {
          prop: 'objName',
          label: '장비명',
          editable: true,
          scopedSlot: 'input-slot',
          sortable: true,
          searchable: true,
          remoteProp: 'o.obj_name',
        },
        {
          prop: 'objtypeId',
          label: '장비 종류',
          sortable: true,
          searchable: true,
          remoteProp: 'o.objtype_Id',
        },
        {
          prop: 'ipaddressApplytype',
          label: 'IP타입',
          editable: true,
          scopedSlot: 'iptype-slot',
          sortable: true,
          searchable: true,
          remoteProp: 'o.ipaddress_applytype',
        },
        {
          prop: 'ipaddress',
          label: 'IP',
          editable: true,
          scopedSlot: 'ipaddress-slot',
          sortable: true,
          searchable: true,
        },
        {
          prop: 'alivecheckTcpPort',
          label: 'TCP 포트',
          editable: true,
          scopedSlot: 'tcp-slot',
          sortable: true,
          searchable: true,
          remoteProp: 'o.alivecheck_tcp_port',
        },
        {
          prop: 'snmpVersion',
          label: 'SNMP 버전',
          editable: true,
          scopedSlot: 'snmp-version-slot',
          searchable: true,
          remoteProp: 'o.snmp_version',
        },
        {
          prop: 'snmpCommunityRead',
          label: 'SNMP 커뮤니티',
          editable: true,
          scopedSlot: 'community-slot',
        },
        {
          prop: 'snmpPort',
          label: 'SNMP 포트',
          editable: true,
          scopedSlot: 'snmp-slot',
          sortable: true,
          searchable: true,
          remoteProp: 'o.snmp_port',
        },
        {
          prop: 'registDatetime',
          label: '장비등록일시',
          searchType: 'date',
          sortable: true,
          searchable: true,
          remoteProp: 'o.regist_datetime',
          formatter: this.getDateFormatter,
        },
      ],
    };
  },
  mounted() {
    this.loadData();

    // SNMP 버전
    BasicinfoSubAPI.getList('SNMPVER').then((response) => {
      this.snmpVersionList = response.data.data;
    });
  },
  methods: {
    loadData() {
      this.selectedRowArray = [];
      const tableParams = this.$refs.rtuTable.getParameter();
      const params = {
        ...tableParams,
      };
      this.isLoading = true;
      DeviceRegistrationFmsApi.getRtuList(params)
        .then((response) => {
          if (response.data.success) {
            const data = cloneDeep(response.data.data);
            this.originalTableData = data;
            this.total = response.data.total;
            this.tableData = this.getEditableTableData(data, this.tableColumn);
            this.resetCellEditing();
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onChangeTable() {
      this.loadData();
    },
    setSearchToolVisible() {
      this.showSearchTool = !this.showSearchTool;
    },
    // 테이블 체크박스 셀렉션 change함수
    changeHandleSelection(selectVal) {
      this.selectedRowArray = selectVal;
    },
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(value);
      }
      return EMPTY_TEXT;
    },
    // table-row double click 이벤트
    onDblClickForSlidingPopup() {
      this.$refs.rtuPop.open();
    },
    onDelRtuDevice() {
      this.$confirm(...msgUtil.getBasicConfirmData(msgUtil.action.DELETE)).then(() => {
        DeviceRegistrationFmsApi.deleteFacilityList(this.selectedRowArray)
          .then((response) => {
            const result = response.data;
            if (result.success) {
              this.loadData();
            }
            this.$message(msgUtil.getResultMsgData(msgUtil.action.DELETE, '수집장비', result.success));
          })
          .catch(() => {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.DELETE, '수집장비', false));
          });
      });
    },
    validateIpaddress(value) {
      return validateIpv4(value) || validateIpv6(value);
    },
    handlerEndEdit() {
      this.checkExistModifiedData(this.tableData);
    },
    /**
     * RTU 장비 정보를 수정한다.
     * @return {[type]} [description]
     * @author jda
     */
    saveRtu() {
      const updatedData = this.getUpdatedDatas(this.tableData, true, ['objId']);
      let ipDuplicatedFlag = false;
      const ipList = [];
      updatedData.forEach((obj) => {
        const ipaddress = obj.ipaddress;
        if (ipList.includes(ipaddress)) {
          ipDuplicatedFlag = true;
        }
        ipList.push(ipaddress);
        if (obj.ipaddressApplytype === 'IPv4') {
          obj.ipaddressIpv4 = ipaddress;
        } else {
          obj.ipaddressIpv6 = ipaddress;
        }
      });

      if (ipDuplicatedFlag) {
        this.$message(msgUtil.getCustomResultMsgData('중복된 IP가 존재합니다.', false));
        return;
      }

      getRsaEncryptBulk(updatedData, ['snmpCommunityRead'])
        .then((encryptData) => {
          DeviceRegistrationFmsApi.updateRtuList(encryptData)
            .then((response) => {
              if (response.data.success) {
                this.loadData();
              }
              this.$message(msgUtil.getResultMsgData(msgUtil.action.SAVE, '수집장비', response.data.success));
            })
            .catch(() => {
              this.$message(msgUtil.getResultMsgData(msgUtil.action.SAVE, '수집장비', false));
            });
        })
        .catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.SAVE, '수집장비', false));
          throw new Error(e);
        });
    },
    /**
     * SNMP 커뮤니티 convert method
     * @param  {[type]} record [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertCommunityRead(record) {
      const snmpVersion = record.snmpVersion;
      if (!isNil(snmpVersion) && snmpVersion !== 'NONE' && snmpVersion !== 'V3') {
        return '******';
      }
      return EMPTY_TEXT;
    },
    endIpaddressEdit(newValue) {
      const data = this.cellEditing.data;
      const property = this.cellEditing.property;

      if (data && property) {
        // TODO: Ediotr의 keyup 이벤트로 endEdit가 호출되는 경우, keyup 이벤트에 endEdit 한번 호출,
        // blur 이벤트에서 한번 호출 총 2번 호출이 가서 2번째에서 editing 객체가 초기화되어 에러남.
        // 일단  data와 property가 있을 경우만 하단의 로직 수행하도록 처리함.

        // ipValidation check
        let dirty = isEqual(this.cellEditing.originalValue, newValue);
        if (!dirty) {
          const params = {
            objManagetypeId: 'FMS',
            ipaddressApplytype: data.ipaddressApplytype,
          };
          if (validateIpv4(newValue)) {
            params.ipaddressIpv4 = newValue;
          } else if (validateIpv6(newValue)) {
            params.ipaddressIpv6 = newValue;
          }
          if (this.validateIpaddress(newValue)) {
            DeviceRegistrationCommonAPI.checkIpaddressValidation(params)
              .then((response) => {
                if (response.data.data) {
                  dirty = this.setModified(data, property, this.cellEditing.originalValue, newValue);
                } else {
                  dirty = this.setModified(data, property, this.cellEditing.originalValue, this.cellEditing.originalValue);
                }
              })
              .catch(() => {})
              .finally(() => {
                this.completeEdit(data, property, dirty);
              });
          }
        } else {
          dirty = this.setModified(data, property, this.cellEditing.originalValue, newValue);
          this.completeEdit(data, property, dirty);
        }
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/settings/basic/DeviceRegistration/FacilityDevice/RtuList.vue